import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate, type SlideSummaryDto } from '@pb/shared';
import { retireSlide, searchSlides, unretireSlide } from '../api/slides';
import { ApiError, errorMessage } from '../api/client';
import { createGroup, getGroup, listGroups, setGroupSlides } from '../api/groups';
import { startImport } from '../api/imports';
import { Dialog } from '../components/Dialog';
import { FilePicker, ProgressBar } from '../components/FilePicker';
import { EmptyState, ErrorAlert, ErrorState, InUseList, Loading } from '../components/States';
import { TagEditorDialog, TagFilter, TagList } from '../components/Tags';
import { ThumbLink } from '../components/Thumb';
import { useProject } from '../lib/project';
import { anyPending } from '../lib/thumbs';
import { pageCountLabel, pluralize } from '../lib/text';
import { useDebounced, usePolling, useResource } from '../lib/useResource';

export function ImportDeckDialog({ onClose }: { onClose: () => void }) {
  const { project } = useProject();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [abort, setAbort] = useState<(() => void) | null>(null);

  const upload = async () => {
    if (!file) return;
    setError(null);
    setProgress(0);
    const handle = startImport(project.id, file, setProgress);
    setAbort(() => handle.abort);
    try {
      const result = await handle.promise;
      navigate(`/projects/${project.id}/imports/${result.id}`);
    } catch (err) {
      setError(err);
      setProgress(null);
      setAbort(null);
    }
  };

  const busy = progress !== null;

  return (
    <Dialog
      title="Import a deck"
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          {busy && abort ? (
            <button type="button" className="btn" onClick={() => abort()}>
              Cancel upload
            </button>
          ) : (
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={() => void upload()} disabled={!file || busy}>
            {busy ? 'Uploading…' : 'Upload and review'}
          </button>
        </>
      }
    >
      <p className="small muted">
        The deck is split into single pages. You review them, combine pages that belong together, form groups, and
        only then commit them to the library. Up to 300 pages.
      </p>
      <FilePicker
        file={file}
        onFile={(f) => {
          setFile(f);
          setError(null);
        }}
        disabled={busy}
      />
      {busy ? <ProgressBar value={progress ?? 0} /> : null}
      <ErrorAlert error={error} />
    </Dialog>
  );
}

function AddToGroupDialog({
  slideIds,
  onClose,
  onDone,
}: {
  slideIds: number[];
  onClose: () => void;
  onDone: (groupId: number, groupName: string) => void;
}) {
  const { project } = useProject();
  const groups = useResource(() => listGroups(project.id), [project.id]);
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [groupId, setGroupId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (mode === 'new') {
        const created = await createGroup(project.id, { name: name.trim(), slide_ids: slideIds });
        onDone(created.id, created.name);
        return;
      }
      if (groupId === '') return;
      const group = await getGroup(groupId);
      const current = group.slides.map((s) => s.slide_id);
      const additions = slideIds.filter((id) => !current.includes(id));
      if (additions.length > 0) {
        await setGroupSlides(group.id, group.version_no, [...current, ...additions], note);
      }
      onDone(group.id, group.name);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  const available = (groups.data ?? []).filter((g) => !g.retired);
  const canSave = mode === 'new' ? name.trim().length > 0 : groupId !== '';

  return (
    <Dialog
      title={`Add ${pluralize(slideIds.length, 'slide')} to a group`}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy || !canSave}>
            {busy ? 'Adding…' : 'Add to group'}
          </button>
        </>
      }
    >
      <div className="row" role="radiogroup" aria-label="Target group">
        <label className="checkbox">
          <input type="radio" name="group-mode" checked={mode === 'existing'} onChange={() => setMode('existing')} />
          Existing group
        </label>
        <label className="checkbox">
          <input type="radio" name="group-mode" checked={mode === 'new'} onChange={() => setMode('new')} />
          New group
        </label>
      </div>
      {mode === 'existing' ? (
        groups.error ? (
          <ErrorState error={groups.error} onRetry={() => void groups.reload()} />
        ) : groups.data === undefined ? (
          <Loading />
        ) : available.length === 0 ? (
          <p className="muted">There are no groups yet. Choose “New group” to create one with these slides.</p>
        ) : (
          <>
            <div className="field">
              <label htmlFor="add-group-select">Group</label>
              <select
                id="add-group-select"
                className="select"
                value={groupId}
                onChange={(event) => setGroupId(event.target.value ? Number(event.target.value) : '')}
              >
                <option value="">Choose a group…</option>
                {available.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} ({pluralize(g.slide_count, 'slide')})
                  </option>
                ))}
              </select>
              <span className="field-hint">The slides are appended at the end; slides already in the group are skipped.</span>
            </div>
            <div className="field">
              <label htmlFor="add-group-note">Change note (optional)</label>
              <input
                id="add-group-note"
                className="input"
                value={note}
                maxLength={2000}
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
          </>
        )
      ) : (
        <div className="field">
          <label htmlFor="new-group-name">Group name</label>
          <input
            id="new-group-name"
            className="input"
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
      )}
      <ErrorAlert error={error} />
    </Dialog>
  );
}

type LibrarySort = 'title' | 'last_change' | 'open_feedback';

interface Refusal {
  slide: SlideSummaryDto;
  error: unknown;
}

function RetireSlidesDialog({
  slides,
  onClose,
  onFinished,
}: {
  slides: SlideSummaryDto[];
  onClose: () => void;
  onFinished: (retired: number) => void;
}) {
  const { project } = useProject();
  const [targets] = useState(slides);
  const [progress, setProgress] = useState<number | null>(null);
  const [outcome, setOutcome] = useState<{ retired: number; refused: Refusal[] } | null>(null);

  const run = async () => {
    const refused: Refusal[] = [];
    let retired = 0;
    for (const [index, slide] of targets.entries()) {
      setProgress(index);
      try {
        await retireSlide(slide.id, slide.title);
        retired += 1;
      } catch (err) {
        refused.push({ slide, error: err });
      }
    }
    setProgress(null);
    onFinished(retired);
    if (refused.length === 0) onClose();
    else setOutcome({ retired, refused });
  };

  const busy = progress !== null;

  return (
    <Dialog
      title={`Retire ${pluralize(targets.length, 'slide')}?`}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        outcome ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-danger" onClick={() => void run()} disabled={busy}>
              {busy ? `Retiring ${(progress ?? 0) + 1} of ${targets.length}…` : 'Retire'}
            </button>
          </>
        )
      }
    >
      {outcome ? (
        <>
          <p>
            {outcome.retired > 0 ? `${pluralize(outcome.retired, 'slide')} retired. ` : ''}
            {outcome.refused.length === 1
              ? 'This slide was not retired:'
              : `These ${outcome.refused.length} slides were not retired:`}
          </p>
          <ul className="list-plain" aria-label="Slides not retired" style={{ maxHeight: 320, overflowY: 'auto' }}>
            {outcome.refused.map(({ slide, error }) => {
              const inUse = error instanceof ApiError ? error.inUse : null;
              return (
                <li key={slide.id} className="alert alert-error small">
                  <div className="stack" style={{ gap: 4 }}>
                    <Link to={`/projects/${project.id}/slides/${slide.id}`} onClick={onClose}>
                      <strong>{slide.title}</strong>
                    </Link>
                    {inUse ? (
                      <div>
                        It is in the latest version of these groups:
                        <InUseList details={inUse} />
                      </div>
                    ) : (
                      <div>{errorMessage(error)}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <>
          <p className="small muted">
            Retired slides are hidden from the library unless “Show retired” is on, and can be unretired at any time.
            A slide in the latest version of a group that is not retired is refused and listed afterwards.
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 20, maxHeight: 240, overflowY: 'auto' }}>
            {targets.map((slide) => (
              <li key={slide.id}>{slide.title}</li>
            ))}
          </ul>
        </>
      )}
    </Dialog>
  );
}

function TagCounts({
  tagIds,
  untagged,
  unused,
  openFeedback,
  counts,
  onToggleTag,
  onToggleUntagged,
  onToggleUnused,
  onToggleOpenFeedback,
}: {
  tagIds: number[];
  untagged: boolean;
  unused: boolean;
  openFeedback: boolean;
  counts: { untagged: number; unused: number; openFeedback: number } | undefined;
  onToggleTag: (id: number) => void;
  onToggleUntagged: () => void;
  onToggleUnused: () => void;
  onToggleOpenFeedback: () => void;
}) {
  const { tags } = useProject();
  const count = (value: number | undefined) => <span className="facet-count">{value ?? '…'}</span>;
  return (
    <aside className="panel stack" style={{ padding: 12, gap: 8 }} aria-label="Tag list">
      <h3 className="small">Tags</h3>
      {tags.length === 0 ? <p className="xsmall faint">This project has no tags yet.</p> : null}
      <ul className="list-plain" style={{ gap: 2 }}>
        {tags.map((tag) => (
          <li key={tag.id}>
            <button
              type="button"
              className="facet"
              aria-pressed={tagIds.includes(tag.id)}
              onClick={() => onToggleTag(tag.id)}
            >
              <span>{tag.name}</span>
              {count(tag.slide_count)}
            </button>
          </li>
        ))}
      </ul>
      <hr className="divider" />
      <ul className="list-plain" style={{ gap: 2 }}>
        <li>
          <button type="button" className="facet" aria-pressed={untagged} onClick={onToggleUntagged}>
            <span>Untagged</span>
            {count(counts?.untagged)}
          </button>
        </li>
        <li>
          <button type="button" className="facet" aria-pressed={unused} onClick={onToggleUnused}>
            <span>Unused</span>
            {count(counts?.unused)}
          </button>
        </li>
        <li>
          <button type="button" className="facet" aria-pressed={openFeedback} onClick={onToggleOpenFeedback}>
            <span>Open feedback</span>
            {count(counts?.openFeedback)}
          </button>
        </li>
      </ul>
    </aside>
  );
}

export function LibraryPage() {
  const { project, tagsVersion, reloadTags } = useProject();
  const [queryText, setQueryText] = useState('');
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [untagged, setUntagged] = useState(false);
  const [unused, setUnused] = useState(false);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [sort, setSort] = useState<LibrarySort>('last_change');
  const [includeRetired, setIncludeRetired] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [dialog, setDialog] = useState<'import' | 'group' | 'tags' | 'retire' | null>(null);
  const [notice, setNotice] = useState<{ text: string; groupId?: number } | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [unretiring, setUnretiring] = useState(false);
  const [countsKey, setCountsKey] = useState(0);
  const debouncedQuery = useDebounced(queryText, 300);

  const slides = useResource(
    () =>
      searchSlides(project.id, {
        query: debouncedQuery,
        tagIds,
        untagged,
        unused,
        hasOpenFeedback: openFeedback,
        includeRetired,
        sort,
      }),
    [project.id, debouncedQuery, tagIds.join(','), untagged, unused, openFeedback, includeRetired, sort],
  );
  const counts = useResource(async () => {
    const [withoutTags, notInGroups, withOpenFeedback] = await Promise.all([
      searchSlides(project.id, { untagged: true }),
      searchSlides(project.id, { unused: true }),
      searchSlides(project.id, { hasOpenFeedback: true }),
    ]);
    return { untagged: withoutTags.length, unused: notInGroups.length, openFeedback: withOpenFeedback.length };
  }, [project.id, tagsVersion, countsKey]);
  const list = slides.data ?? [];
  usePolling(anyPending(list.map((s) => s.thumbnail)), () => void slides.refresh());

  const toggle = (id: number) =>
    setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  const visibleSelected = selected.filter((id) => list.some((s) => s.id === id));
  const filtered = debouncedQuery.trim().length > 0 || tagIds.length > 0 || untagged || unused || openFeedback;

  const chooseTags = (ids: number[]) => {
    setTagIds(ids);
    if (ids.length > 0) setUntagged(false);
  };
  const toggleTag = (id: number) => chooseTags(tagIds.includes(id) ? tagIds.filter((x) => x !== id) : [...tagIds, id]);
  const toggleUntagged = () => {
    if (!untagged) setTagIds([]);
    setUntagged(!untagged);
  };

  const afterLibraryChange = () => {
    setSelected([]);
    setCountsKey((k) => k + 1);
    void slides.refresh();
    void reloadTags();
  };

  const selectedSlides: SlideSummaryDto[] = list.filter((s) => visibleSelected.includes(s.id));
  const commonTags = selectedSlides.length
    ? (selectedSlides[0]?.tags ?? [])
        .map((t) => t.id)
        .filter((id) => selectedSlides.every((s) => s.tags.some((t) => t.id === id)))
    : [];
  const activeSelected = selectedSlides.filter((s) => !s.retired);
  const retiredSelected = selectedSlides.filter((s) => s.retired);

  const unretireSelected = async () => {
    setUnretiring(true);
    setActionError(null);
    setNotice(null);
    let done = 0;
    let firstError: unknown = null;
    for (const slide of retiredSelected) {
      try {
        await unretireSlide(slide.id);
        done += 1;
      } catch (err) {
        firstError ??= err;
      }
    }
    setUnretiring(false);
    if (done > 0) setNotice({ text: `${pluralize(done, 'slide')} unretired.` });
    if (firstError) setActionError(firstError);
    afterLibraryChange();
  };

  return (
    <div className="section">
      <div className="page-header">
        <div className="page-title">
          <h2>Library</h2>
          <p className="muted small">Slides of this project. A slide can have several pages and many versions.</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-primary" onClick={() => setDialog('import')}>
            Import deck
          </button>
        </div>
      </div>

      <div className="toolbar">
        <input
          type="search"
          className="input input-search"
          placeholder="Search titles, page text and notes"
          aria-label="Search titles, page text and notes"
          value={queryText}
          onChange={(event) => setQueryText(event.target.value)}
          style={{ width: 320 }}
        />
        <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <label htmlFor="library-sort" className="small muted">
            Sort
          </label>
          <select
            id="library-sort"
            className="select"
            style={{ width: 190 }}
            value={sort}
            onChange={(event) => setSort(event.target.value as LibrarySort)}
          >
            <option value="last_change">Last change</option>
            <option value="title">Title</option>
            <option value="open_feedback">Most open feedback</option>
          </select>
        </div>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={includeRetired}
            onChange={(event) => setIncludeRetired(event.target.checked)}
            aria-label="Show retired"
          />
          Show retired
        </label>
        <div className="row-wrap" role="group" aria-label="Filters">
          <button type="button" className="chip" aria-pressed={untagged} onClick={toggleUntagged}>
            Untagged
          </button>
          <button type="button" className="chip" aria-pressed={unused} onClick={() => setUnused(!unused)}>
            Unused
          </button>
          <button type="button" className="chip" aria-pressed={openFeedback} onClick={() => setOpenFeedback(!openFeedback)}>
            Open feedback
          </button>
        </div>
        <span className="spacer" />
        {list.length > 0 ? (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => setSelected(visibleSelected.length === list.length ? [] : list.map((s) => s.id))}
          >
            {visibleSelected.length === list.length ? 'Clear selection' : 'Select all'}
          </button>
        ) : null}
      </div>
      <TagFilter selected={tagIds} onChange={chooseTags} />

      {notice ? (
        <div className="alert alert-success row-between" role="status">
          <span>
            {notice.text}{' '}
            {notice.groupId ? <Link to={`/projects/${project.id}/groups/${notice.groupId}`}>Open the group</Link> : null}
          </span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      <ErrorAlert error={actionError} />

      <div className="library-body">
        <TagCounts
          tagIds={tagIds}
          untagged={untagged}
          unused={unused}
          openFeedback={openFeedback}
          counts={counts.data}
          onToggleTag={toggleTag}
          onToggleUntagged={toggleUntagged}
          onToggleUnused={() => setUnused(!unused)}
          onToggleOpenFeedback={() => setOpenFeedback(!openFeedback)}
        />
        <div className="stack">
          {slides.error ? (
            <ErrorState error={slides.error} onRetry={() => void slides.reload()} />
          ) : slides.data === undefined ? (
            <Loading label="Loading slides…" />
          ) : list.length === 0 ? (
            filtered ? (
              <EmptyState title="No slides match">Change the search text or the filters.</EmptyState>
            ) : (
              <EmptyState
                title="The library is empty"
                action={
                  <button type="button" className="btn btn-primary" onClick={() => setDialog('import')}>
                    Import a deck
                  </button>
                }
              >
                Import an existing PowerPoint deck to split it into slides.
              </EmptyState>
            )
          ) : (
            <div className="grid-cards">
              {list.map((slide) => {
                const isSelected = visibleSelected.includes(slide.id);
                return (
                  <article key={slide.id} className={`tile${isSelected ? ' tile-selected' : ''}`}>
                    <label className="tile-select checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggle(slide.id)}
                        aria-label={`Select ${slide.title}`}
                      />
                    </label>
                    <ThumbLink
                      to={`/projects/${project.id}/slides/${slide.id}`}
                      thumb={slide.thumbnail}
                      alt={slide.title}
                      badge={pageCountLabel(slide.page_count, slide.hidden_page_count)}
                      onRetried={() => void slides.refresh()}
                    />
                    <Link to={`/projects/${project.id}/slides/${slide.id}`} className="tile-title" title={slide.title}>
                      {slide.title}
                    </Link>
                    <div className="row-wrap">
                      {slide.retired ? <span className="badge badge-warning">Retired</span> : null}
                      {slide.open_feedback_count > 0 ? (
                        <span className="badge badge-warning" title="Open feedback">
                          {slide.open_feedback_count} open
                        </span>
                      ) : null}
                      <span className="xsmall faint">
                        v{slide.version_no} · {formatDate(slide.updated_at)}
                      </span>
                    </div>
                    <TagList tags={slide.tags} />
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {visibleSelected.length > 0 ? (
        <div className="selection-bar" role="region" aria-label="Selection actions">
          <strong>{pluralize(visibleSelected.length, 'slide')} selected</strong>
          <span className="spacer" />
          <button type="button" className="btn btn-sm" onClick={() => setDialog('group')}>
            Add to group…
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setDialog('tags')}>
            Set tags…
          </button>
          {activeSelected.length > 0 ? (
            <button type="button" className="btn btn-sm" onClick={() => setDialog('retire')} disabled={unretiring}>
              Retire…
            </button>
          ) : null}
          {includeRetired && retiredSelected.length > 0 ? (
            <button type="button" className="btn btn-sm" onClick={() => void unretireSelected()} disabled={unretiring}>
              {unretiring ? 'Unretiring…' : 'Unretire'}
            </button>
          ) : null}
          <button type="button" className="btn btn-sm" onClick={() => setSelected([])}>
            Clear
          </button>
        </div>
      ) : null}

      {dialog === 'import' ? <ImportDeckDialog onClose={() => setDialog(null)} /> : null}
      {dialog === 'group' ? (
        <AddToGroupDialog
          slideIds={visibleSelected}
          onClose={() => setDialog(null)}
          onDone={(groupId, groupName) => {
            setDialog(null);
            setSelected([]);
            setCountsKey((k) => k + 1);
            setNotice({ text: `Added to “${groupName}”.`, groupId });
          }}
        />
      ) : null}
      {dialog === 'tags' ? (
        <TagEditorDialog
          target="slide"
          ids={visibleSelected}
          initial={commonTags}
          title={`Set tags on ${pluralize(visibleSelected.length, 'slide')}`}
          hint="The chosen tags replace the current tags of every selected slide."
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            setNotice({ text: 'Tags saved.' });
            setCountsKey((k) => k + 1);
            void slides.refresh();
          }}
        />
      ) : null}
      {dialog === 'retire' ? (
        <RetireSlidesDialog
          slides={activeSelected}
          onClose={() => setDialog(null)}
          onFinished={(retired) => {
            setActionError(null);
            setNotice(retired > 0 ? { text: `${pluralize(retired, 'slide')} retired.` } : null);
            afterLibraryChange();
          }}
        />
      ) : null}
    </div>
  );
}
